/**
 * API Configuration
 * Reads from global APP_CONFIG (set by shared/config/app-config.ts)
 *
 * This package is consumed by multiple apps, some of which own a stricter
 * global `Window.APP_CONFIG` typing of their own (see flow-studio's
 * shared/config/app-config.ts). To avoid declaration-merging conflicts with
 * those app-owned types, this file reads window.APP_CONFIG defensively
 * rather than asserting its own global Window augmentation.
 */

interface KnownAppConfigFields {
  API_BASE_URL?: string;
  API_TIMEOUT?: number;
  TENANT_ID?: number;
  API_TOKEN?: string;
  [key: string]: unknown;
}

const readAppConfig = (): KnownAppConfigFields | undefined =>
  (window as unknown as { APP_CONFIG?: KnownAppConfigFields }).APP_CONFIG;

export interface AtlasApiConfig {
  /** Base URL for the API server */
  baseUrl: string;
  /** Request timeout in milliseconds (default 30000) */
  timeout: number;
  /** Tenant ID injected into every request as X-Tenant-ID header */
  tenantId: number;
  /** Bearer token injected into every request as Authorization header */
  token: string;
  /** Default headers applied to every request */
  headers: Record<string, string>;
}

/**
 * Resolved API configuration from global APP_CONFIG.
 * Override at runtime via updateAtlasApiConfig().
 */
export const atlasApiConfig: AtlasApiConfig = {
  baseUrl: (readAppConfig()?.API_BASE_URL as string | undefined) ?? '/',
  timeout: (readAppConfig()?.API_TIMEOUT as number | undefined) ?? 30000,
  tenantId: (readAppConfig()?.TENANT_ID as number | undefined) ?? 1,
  token: (readAppConfig()?.API_TOKEN as string | undefined) ?? '',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
};

/**
 * Update configuration at runtime (e.g. after auth).
 */
export const updateAtlasApiConfig = (patch: Partial<AtlasApiConfig>): void => {
  Object.assign(atlasApiConfig, patch);
};

/**
 * Read-only snapshot of current configuration.
 */
export const getAtlasApiConfig = (): Readonly<AtlasApiConfig> =>
  Object.freeze({ ...atlasApiConfig });
